import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, CheckCheck, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { OPS } from "@/constants/testIds";

const POLL_MS = 45000;

function timeAgo(iso, lang) {
    if (!iso) return "";
    const d = new Date(iso.replace(" ", "T"));
    const s = Math.max(1, Math.floor((Date.now() - d.getTime()) / 1000));
    const ar = lang === "ar";
    if (s < 60) return ar ? "الآن" : "just now";
    const m = Math.floor(s / 60);
    if (m < 60) return ar ? `منذ ${m} د` : `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return ar ? `منذ ${h} س` : `${h}h ago`;
    const dd = Math.floor(h / 24);
    return ar ? `منذ ${dd} يوم` : `${dd}d ago`;
}

export default function NotificationInbox() {
    const { t, lang } = useI18n();
    const navigate = useNavigate();
    const [open, setOpen] = useState(false);
    const [items, setItems] = useState([]);
    const [unread, setUnread] = useState(0);
    const [loading, setLoading] = useState(false);

    const load = useCallback(async () => {
        try {
            const r = await api.get("/notifications", { params: { limit: 30 } });
            setItems(r.data.items || []);
            setUnread(r.data.unread_count || 0);
        } catch (e) { /* silent — a failed poll must not disrupt the shell */ }
    }, []);

    useEffect(() => {
        load();
        const id = setInterval(load, POLL_MS);
        return () => clearInterval(id);
    }, [load]);

    useEffect(() => { if (open) load(); }, [open, load]);

    const onItem = async (n) => {
        try { if (!n.read) await api.post(`/notifications/${n.id}/read`); } catch (e) { /* ignore */ }
        setOpen(false);
        await load();
        if (n.link) {
            const path = n.link.replace(/^https?:\/\/[^/]+/, "");
            navigate(path);
        }
    };

    const markAll = async () => {
        setLoading(true);
        try { await api.post("/notifications/read-all"); await load(); }
        finally { setLoading(false); }
    };

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <button
                    data-testid={OPS.shell.notifBell}
                    aria-label={t("notifications")}
                    className="relative flex h-9 w-9 items-center justify-center rounded-full hover:bg-[color:var(--brand-orange-soft)] transition-colors duration-150"
                >
                    <Bell className="h-5 w-5 text-[color:var(--ink)]" />
                    {unread > 0 ? (
                        <span
                            data-testid={OPS.shell.notifBadge}
                            className="absolute -top-0.5 -end-0.5 min-w-[18px] h-[18px] px-1 flex items-center justify-center rounded-full bg-[color:var(--danger)] text-white text-[10px] font-bold leading-none tnum"
                        >
                            {unread > 99 ? "99+" : unread}
                        </span>
                    ) : null}
                </button>
            </PopoverTrigger>
            <PopoverContent
                align="end"
                sideOffset={8}
                data-testid={OPS.shell.notifPanel}
                className="w-[360px] max-w-[92vw] p-0 overflow-hidden"
            >
                <div className="flex items-center justify-between px-4 py-3 border-b border-[color:var(--border-soft)]">
                    <span className="text-sm font-bold text-[color:var(--ink)]">{t("notif_inbox_title")}</span>
                    {unread > 0 ? (
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={markAll}
                            disabled={loading}
                            data-testid={OPS.shell.notifMarkAll}
                            className="h-7 gap-1 text-[11px] text-[color:var(--ink-muted)]"
                        >
                            {loading ? <Loader2 className="h-3 w-3 animate-spin" /> : <CheckCheck className="h-3.5 w-3.5" />}
                            {t("notif_mark_all_read")}
                        </Button>
                    ) : null}
                </div>

                <div className="max-h-[420px] overflow-y-auto">
                    {items.length === 0 ? (
                        <div className="px-4 py-10 text-center text-[13px] text-[color:var(--ink-muted)]">
                            <Bell className="h-6 w-6 mx-auto mb-2 opacity-40" />
                            {t("notif_empty")}
                        </div>
                    ) : (
                        items.map((n) => (
                            <button
                                key={n.id}
                                onClick={() => onItem(n)}
                                data-testid={OPS.shell.notifItem(n.id)}
                                className={`w-full text-start px-4 py-3 border-b border-[color:var(--border-soft)] transition-colors hover:bg-[color:var(--brand-orange-soft)] ${n.read ? "" : "bg-[color:rgba(232,168,80,0.08)]"}`}
                            >
                                <div className="flex items-start gap-2">
                                    {!n.read ? <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-[color:var(--brand-orange)]" /> : <span className="mt-1.5 h-2 w-2 shrink-0" />}
                                    <div className="min-w-0 flex-1">
                                        <div className="text-[13px] font-semibold text-[color:var(--ink)] leading-snug">
                                            <bdi>{lang === "ar" ? n.title_ar : n.title_en}</bdi>
                                        </div>
                                        <div className="text-[12px] text-[color:var(--ink-muted)] leading-snug mt-0.5">
                                            <bdi>{lang === "ar" ? n.body_ar : n.body_en}</bdi>
                                        </div>
                                        <div className="text-[10px] text-[color:var(--ink-muted)] mt-1">{timeAgo(n.created_at, lang)}</div>
                                    </div>
                                </div>
                            </button>
                        ))
                    )}
                </div>
            </PopoverContent>
        </Popover>
    );
}
